import { useState } from 'react';
import { Link } from 'react-router-dom';
import { fileTicket, getMine } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { boardGroup, timeAgo } from '../lib/format';
import { KindChip, StatusPill } from '../components/Chips';
import type { NewTicket } from '../lib/api';

const URGENCIES: { value: NewTicket['urgency']; label: string; hint: string }[] = [
  { value: 'blocking', label: 'Blocking now', hint: 'a room or a customer is affected right now' },
  { value: 'soon', label: 'Should be fixed', hint: 'annoying, but there is a way around it' },
  { value: 'idea', label: 'Idea or request', hint: 'no rush' },
];

export default function ReportPage() {
  const mine = useAsync(getMine, []);
  const [title, setTitle] = useState('');
  const [detail, setDetail] = useState('');
  const [where, setWhere] = useState('');
  const [kind, setKind] = useState<NewTicket['kind']>('bug');
  const [urgency, setUrgency] = useState<NewTicket['urgency']>('soon');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filed, setFiled] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { item } = await fileTicket({ title: title.trim(), detail: detail.trim(), kind, urgency, where: where.trim() });
      setFiled(item.key);
      setTitle('');
      setDetail('');
      setWhere('');
      mine.reload();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <h1>Report something</h1>
      <p className="sub">
        This goes straight onto the developers' board. For a problem in a room right now, the Mark issue button
        in the operator app is faster: it captures the PC, the run and the versions by itself.
      </p>

      {filed ? (
        <div className="panel" style={{ borderColor: 'var(--green)', marginBottom: 18 }}>
          Filed as <Link to={`/item/${filed}`}>{filed}</Link>. You can follow it below.
        </div>
      ) : null}
      {error ? <div className="error">{error}</div> : null}

      <form className="panel" onSubmit={submit}>
        <label htmlFor="title">What is it?</label>
        <input
          id="title"
          type="text"
          placeholder="One line, as you would say it out loud"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />

        <label htmlFor="detail" style={{ marginTop: 14 }}>
          What happened, and what did you expect?
        </label>
        <textarea id="detail" rows={5} value={detail} onChange={(e) => setDetail(e.target.value)} />

        <label htmlFor="where" style={{ marginTop: 14 }}>
          Where? (optional)
        </label>
        <input
          id="where"
          type="text"
          placeholder="Room, PC, headset, or the page you were on"
          value={where}
          onChange={(e) => setWhere(e.target.value)}
        />

        <div className="row" style={{ marginTop: 16, gap: 24 }}>
          <span>
            <label htmlFor="kind">Kind</label>
            <select id="kind" value={kind} onChange={(e) => setKind(e.target.value as NewTicket['kind'])}>
              <option value="bug">Something is broken</option>
              <option value="feature">Something is missing</option>
            </select>
          </span>
          <span style={{ flex: 1 }}>
            <label htmlFor="urgency">How urgent?</label>
            <select
              id="urgency"
              value={urgency}
              onChange={(e) => setUrgency(e.target.value as NewTicket['urgency'])}
            >
              {URGENCIES.map((u) => (
                <option key={u.value} value={u.value}>
                  {u.label}: {u.hint}
                </option>
              ))}
            </select>
          </span>
        </div>

        <button disabled={busy || title.trim().length < 4} style={{ marginTop: 16 }}>
          {busy ? 'Sending...' : 'File it'}
        </button>
      </form>

      <h2>Your tickets</h2>
      {mine.loading ? <p className="loading">Loading...</p> : null}
      {mine.error ? <div className="error">{mine.error}</div> : null}
      {mine.data && !mine.data.items.length ? <p className="empty">Nothing yet.</p> : null}
      {mine.data?.items.map((item) => (
        <Link className="result" key={item.id} to={`/item/${item.key}`}>
          <div className="rtitle">
            {item.title} <StatusPill status={boardGroup(item)} /> <KindChip kind={item.kind} />
          </div>
          <div className="rpath">
            {item.key}, filed {timeAgo(item.created_at)}
          </div>
        </Link>
      ))}
    </>
  );
}
